import { zodResolver } from '@hookform/resolvers/zod';
import { VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import { isValidIBAN } from 'ibantools';
import { ComponentProps, ReactNode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Button } from '../../../../../design-system/button/button';
import { FormTextField } from '../../../../../design-system/form-text-field/form-text-field';
import { Form } from '../../../../../design-system/form/form-primitives';
import { Stack } from '../../../../../design-system/stack/stack';
import { cn } from '../../../../../utils/cn';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';
import { PayeeCheckLoadingCard } from '../../../../payments/components/payee-check-loading-card';
import { PayeeCheckResultCard } from '../../financial-information-step/payee-check-result-card';

const ibanSchema = (required: boolean) => {
  const base = z.string().trim();

  const withValidation = required ? base.min(1, { message: 'IBAN is required' }) : base;
  const withIbanValidation = withValidation.refine((val) => !val || isValidIBAN(val), {
    message: 'Invalid IBAN',
  });
  const transformed = withIbanValidation.transform((val) => val.split(' ').join(''));

  return required ? transformed : transformed.optional();
};

const accountNumberSchema = (required: boolean) => {
  const base = z.string().trim().max(8, { message: 'Max 8 characters' });

  const withValidation = required ? base.min(1, { message: 'Account number is required' }) : base;
  const transformed = withValidation.transform((val) => val.replace(/[^0-9]/g, ''));

  return required ? transformed : transformed.optional();
};

const sortCodeSchema = (required: boolean) => {
  const base = z.string().trim().max(8, { message: 'Max 8 characters' });

  const withValidation = required ? base.min(1, { message: 'Sort code is required' }) : base;
  const transformed = withValidation.transform((val) => val.replace(/[^0-9]/g, ''));

  return required ? transformed : transformed.optional();
};

const bankAccountDetailsFormSchema = (isCurrencyGbp: boolean) =>
  z.object({
    fname: z.string().trim().min(1, { message: 'First name is required' }),
    lname: z.string().trim().min(1, { message: 'Last name is required' }),
    iban: ibanSchema(!isCurrencyGbp),
    accountNumber: accountNumberSchema(isCurrencyGbp),
    sortCode: sortCodeSchema(isCurrencyGbp),
  });

export type BankAccountDetailsFormValues = z.infer<ReturnType<typeof bankAccountDetailsFormSchema>>;

type CompletedCheckConfig =
  | {
      completionStatus: 'MATCHED';
      code: VerifyPayeeCheckResponseCode.MATCHED;
      onSubmit: (data: BankAccountDetailsFormValues) => Promise<void>;
    }
  | {
      completionStatus: 'CLOSE_MATCH';
      code: VerifyPayeeCheckResponseCode.CLOSE_MATCH;
      matchedName?: string;
      onSubmit: (data: BankAccountDetailsFormValues) => Promise<void>;
      onContinue: () => void;
    }
  | {
      completionStatus: 'GENERAL';
      code: Exclude<
        VerifyPayeeCheckResponseCode,
        VerifyPayeeCheckResponseCode.MATCHED | VerifyPayeeCheckResponseCode.CLOSE_MATCH
      >;
      onSubmit: (data: BankAccountDetailsFormValues) => Promise<void>;
      onContinue: () => void;
    };

type PayeeCheckCompletionConfig =
  | {
      status: 'IDLE';
      onSubmit: (data: BankAccountDetailsFormValues) => Promise<void>;
    }
  | {
      status: 'LOADING';
    }
  | {
      status: 'COMPLETED';
      completedCheckConfig: CompletedCheckConfig;
    };

type BankAccountDetailsFormArgs = {
  initialValues: BankAccountDetailsFormValues;
  isCurrencyGbp: boolean;
  payeeCheckCompletionConfig: PayeeCheckCompletionConfig;
};

export const BankAccountDetailsForm = ({
  initialValues,
  isCurrencyGbp = false,
  payeeCheckCompletionConfig,
}: BankAccountDetailsFormArgs) => {
  const isPayeeCheckLoading = payeeCheckCompletionConfig.status === 'LOADING';
  const buttonConfig = getButtonConfig(payeeCheckCompletionConfig);

  const form = useForm<BankAccountDetailsFormValues>({
    resolver: zodResolver(bankAccountDetailsFormSchema(isCurrencyGbp)),
    defaultValues: initialValues,
    mode: 'onBlur',
    disabled: isPayeeCheckLoading,
  });

  useEffect(() => {
    form.trigger();
  }, [form]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          const { status } = payeeCheckCompletionConfig;

          switch (status) {
            case 'IDLE':
              payeeCheckCompletionConfig.onSubmit(data);

              return;
            case 'LOADING':
              return;
            case 'COMPLETED':
              payeeCheckCompletionConfig.completedCheckConfig.onSubmit(data);

              return;
            default:
              return exhaustiveCheck(status);
          }
        })}
        className="h-full"
      >
        <Stack justify="between" align="stretch" className="h-full">
          <Stack space={4} align="stretch">
            {isPayeeCheckLoading && <PayeeCheckLoadingCard />}

            {payeeCheckCompletionConfig.status === 'COMPLETED' && (
              <PayeeCheckResultCard
                variantConfig={getCardVariantConfig(payeeCheckCompletionConfig.completedCheckConfig)}
              />
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FieldWrapper>
                <FormTextField label="First Name" inputName="fname" placeholder="Enter first name" />
              </FieldWrapper>

              <FieldWrapper>
                <FormTextField label="Last Name" inputName="lname" placeholder="Enter last name" />
              </FieldWrapper>

              {isCurrencyGbp && (
                <>
                  <FieldWrapper>
                    <FormTextField
                      label="Account Number"
                      inputName="accountNumber"
                      placeholder="Enter account number"
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <FormTextField label="Sort Code" inputName="sortCode" placeholder="Enter sort code" />
                  </FieldWrapper>
                </>
              )}

              {!isCurrencyGbp && (
                <FieldWrapper className="sm:col-span-2">
                  <FormTextField label="IBAN" inputName="iban" placeholder="Enter IBAN" />
                </FieldWrapper>
              )}
            </div>
          </Stack>

          <Stack space={2} align="stretch">
            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={!form.formState.isValid}
              isLoading={form.formState.isSubmitting || isPayeeCheckLoading}
            >
              {buttonConfig.primaryConfig.text}
            </Button>

            {payeeCheckCompletionConfig.status === 'COMPLETED' &&
              payeeCheckCompletionConfig.completedCheckConfig.completionStatus !== 'MATCHED' &&
              buttonConfig.secondaryConfig.isVisible && (
                <Button
                  variant="destructive"
                  size="lg"
                  onClick={payeeCheckCompletionConfig.completedCheckConfig.onContinue}
                >
                  {buttonConfig.secondaryConfig.text}
                </Button>
              )}
          </Stack>
        </Stack>
      </form>
    </Form>
  );
};

type FieldWrapperProps = {
  children: ReactNode;
  className?: string;
};

const FieldWrapper = ({ children, className }: FieldWrapperProps) => {
  return <div className={cn('min-w-52 flex-1', className)}>{children}</div>;
};

const getCardVariantConfig = (config: CompletedCheckConfig) => {
  const { code } = config;

  switch (code) {
    case VerifyPayeeCheckResponseCode.CLOSE_MATCH:
      return { variant: code, name: config.matchedName };
    case VerifyPayeeCheckResponseCode.MATCHED:
    case VerifyPayeeCheckResponseCode.NO_MATCH:
    case VerifyPayeeCheckResponseCode.CHECK_NOT_POSSIBLE:
    case VerifyPayeeCheckResponseCode.INVALID:
    case VerifyPayeeCheckResponseCode.GENERAL:
    case VerifyPayeeCheckResponseCode.INTERNAL_ERROR:
      return { variant: code };
    default:
      return exhaustiveCheck(code);
  }
};

const getButtonConfig = (config: ComponentProps<typeof BankAccountDetailsForm>['payeeCheckCompletionConfig']) => {
  const { status } = config;

  switch (status) {
    case 'IDLE':
    case 'LOADING':
      return { primaryConfig: { text: 'Verify' }, secondaryConfig: { isVisible: false } };
    case 'COMPLETED': {
      const { completionStatus: checkStatusConfig } = config.completedCheckConfig;

      switch (checkStatusConfig) {
        case 'MATCHED':
          return { primaryConfig: { text: 'Save' }, secondaryConfig: { isVisible: false } };
        case 'CLOSE_MATCH':
        case 'GENERAL':
          return {
            primaryConfig: { text: 'Change and Save' },
            secondaryConfig: { isVisible: true, text: 'Continue Anyway' },
          };
        default:
          return exhaustiveCheck(checkStatusConfig);
      }
    }
    default:
      return exhaustiveCheck(status);
  }
};
